// Fitness, fatigue and form, from Strava's Relative Effort (a heart-rate load score), and the
// same three projected to Day 1 along the plan and the ramp. The standard model: fitness is a
// 42-day weighted average of daily load, fatigue a 7-day one, form the gap between them.
// Measured up to today; everything after today is an estimate from the plan, and is marked so.
import { goalDate } from "./goal.ts";
import type { State, Ride } from "./types";
import { addDays, daysBetween, mondayOf, dow } from "./dates.ts";

export type FitDay = { d: string; load: number; ctl: number; atl: number; tsb: number; proj: boolean };
export type Fitness = {
  days: FitDay[];
  now: FitDay | null;
  day1: FitDay | null;          // the morning of Day 1, projected
  peak: FitDay | null;          // highest measured fitness so far
  perKm: number;                // load per km, from his rides: how a planned ride becomes load
  from: "strava" | "intervals";
};

const KC = 1 - Math.exp(-1 / 42), KA = 1 - Math.exp(-1 / 7);
const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : 0; };
const r1 = (x: number) => Math.round(x * 10) / 10;

/** How much load a kilometre carries for him, from rides that have a Relative Effort. */
export function loadPerKm(rides: Ride[]): number {
  const xs = rides.filter((r) => r.re && r.km >= 15).slice(0, 20).map((r) => r.re! / r.km);
  return xs.length >= 3 ? r1(median(xs)) : 4.5;
}

/** The planned load on each future day: the booked and proposed rides first, then the ramp. */
export function plannedLoad(s: State, today: string, until: string, perKm: number): Map<string, number> {
  const out = new Map<string, number>();
  const planned = new Set<string>();
  for (const w of s.plan) for (const d of w.days) {
    if (d.date <= today || d.date > until) continue;
    planned.add(mondayOf(d.date));
    if (d.type !== "ride" || d.status === "cancelled" || !d.start || !d.end) continue;
    const route = s.routes.find((r) => r.id === d.route);
    const km = route?.km ?? ((toMin(d.end) - toMin(d.start)) / 60) * 22; // the city loop: its time at his pace
    out.set(d.date, (out.get(d.date) ?? 0) + km * perKm);
  }
  const recentKm = median(s.rides.filter((r) => r.km >= 15).slice(0, 10).map((r) => r.km)) || 45;
  for (const w of s.ramp) {
    if (planned.has(w.w) || addDays(w.w, 6) <= today || w.w > until) continue;
    const n = w.rides ?? 3;
    const km = w.km ?? n * recentKm;
    // The week's shape: the long ride on Sunday, the rest midweek.
    const split: [number, number][] = n <= 1 ? [[6, 1]] : n === 2 ? [[3, 0.4], [6, 0.6]] : [[1, 0.25], [3, 0.25], [6, 0.5]];
    for (const [i, f] of split) { const d = addDays(w.w, i); if (d > today && d <= until) out.set(d, (out.get(d) ?? 0) + km * f * perKm); }
  }
  return out;
}
const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

export type Icu = { days: { d: string; ctl: number; atl: number }[]; perKm: number | null };
/** With intervals.icu, the measured days are its CTL and ATL and the projection uses its load per km;
 *  without it, everything is estimated from Strava's Relative Effort. */
export function fitness(s: State, today: string, icu?: Icu | null): Fitness {
  const rides = s.rides.filter((r) => r.km >= 1);
  // Only once intervals.icu actually holds rides: an empty account reads as fitness 0 every day.
  const useIcu = !!icu && icu.days.some((x) => x.ctl > 0);
  const icuDay = new Map((icu?.days ?? []).map((x) => [x.d, x]));
  const perKm = useIcu && icu!.perKm ? icu!.perKm : loadPerKm(rides);
  // Projected to the goal's first day; with no goal, twelve weeks ahead.
  const day1Date = goalDate(s) ?? addDays(today, 84);
  const first = useIcu ? icu!.days[0].d : rides.reduce((m, r) => (r.d < m ? r.d : m), today);
  const start = addDays(first, -1);
  const measured = new Map<string, number>();
  for (const r of rides) measured.set(r.d, (measured.get(r.d) ?? 0) + (r.re ?? r.km * perKm));
  const future = plannedLoad(s, today, addDays(day1Date, -1), perKm);

  const days: FitDay[] = [];
  let ctl = 0, atl = 0;
  const n = daysBetween(start, day1Date);
  for (let i = 0; i <= n; i++) {
    const d = addDays(start, i);
    const proj = d > today;
    const load = d === day1Date ? 0 : proj ? future.get(d) ?? 0 : measured.get(d) ?? 0;
    const tsb = ctl - atl; // form on the morning of d: yesterday's fitness less yesterday's fatigue
    ctl += (load - ctl) * KC;
    atl += (load - atl) * KA;
    const m = !proj && useIcu ? icuDay.get(d) : undefined;
    if (m) { ctl = m.ctl; atl = m.atl; }
    days.push({ d, load: Math.round(load), ctl: r1(ctl), atl: r1(atl), tsb: r1(tsb), proj });
  }
  const now = days.find((x) => x.d === today) ?? null;
  const day1 = days.find((x) => x.d === day1Date) ?? null;
  const peak = days.filter((x) => !x.proj).reduce<FitDay | null>((m, x) => (!m || x.ctl > m.ctl ? x : m), null);
  return { days, now, day1, peak, perKm, from: useIcu ? "intervals" : "strava" };
}

/** One line on what the curve says, forward, for the card under the chart. */
export function fitnessLine(f: Fitness): string {
  if (!f.now || !f.day1) return "Not enough rides yet to draw fitness.";
  const peak = f.peak && f.peak.ctl > f.now.ctl + 3 ? ` Your summer peak was ${Math.round(f.peak.ctl)}.` : "";
  // Form read against fitness, since Relative Effort runs on its own scale.
  const pctForm = f.day1.ctl > 0 ? (f.day1.tsb / f.day1.ctl) * 100 : 0;
  const form = pctForm > 30 ? "very fresh, perhaps over-rested" : pctForm >= 5 ? "fresh" : pctForm >= -10 ? "ready" : "carrying fatigue";
  return `Fitness ${Math.round(f.now.ctl)} today. Riding the plan, it reaches ${Math.round(f.day1.ctl)} by 12 June, and you start Day 1 ${form} (form ${f.day1.tsb > 0 ? "+" : ""}${Math.round(f.day1.tsb)}, an estimate).${peak}`;
}

export const _test = { KC, KA, dow };
